import { BodyText, Spinner, Subtitle } from '@kvdbil/components';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ForgotPassword from '~/App/shared/components/ForgotPassword';
import Head, { Props as HeadProps } from '~/App/shared/components/Head';
import Section from '~/App/shared/components/Layout/Section';
import { useParamFromQuery } from '~/App/shared/hooks/useQueryParams';
import { validateResetPasswordToken as validateResetPasswordTokenCall } from '~/helpers/orchestration/password';
import { useTranslation } from '~/Locale';
import ResetPasswordForm from '~/App/shared/components/ResetPasswordForm';

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 2rem;
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const StyledSubtitle = styled(Subtitle)`
  font-weight: ${({ theme }) => theme.typography.fontAlternativeBoldWeight};
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
`;

const StyledSpinner = styled(Spinner)`
  margin: 0 auto;
`;

interface Props {
  data?: HeadProps;
}

const ResetPassword = ({ data }: Props) => {
  const [isValidToken, setValidToken] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const { t } = useTranslation();
  const token = useParamFromQuery('token') ?? '';

  const validateResetPasswordToken = (resetPasswordToken: string) => {
    validateResetPasswordTokenCall(resetPasswordToken)
      .then(response => {
        setValidToken(response.data.isValidToken);
        setLoading(false);
      })
      .catch(() => {
        setValidToken(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    validateResetPasswordToken(token);
  }, [token]);

  if (isLoading) {
    return (
      <SpinnerContainer>
        <StyledSpinner color="neutral" speed="fast" />
      </SpinnerContainer>
    );
  }

  if (isValidToken) {
    return (
      <Container>
        <Head {...data} title={t('PAGE_TITLES.RESET_PASSWORD')} />

        <Section
          backgroundColor="gray"
          maxWidth={32}
          contentBackgroundColor="light"
        >
          <Content>
            <Description>
              <StyledSubtitle as="h3">{t('Reset password')}</StyledSubtitle>
              <BodyText>{t('Enter a new password.')}</BodyText>
            </Description>
            <ResetPasswordForm
              displayTermsCheckbox={false}
              token={token}
              redirectTo="/"
            />
          </Content>
        </Section>
      </Container>
    );
  }

  return <ForgotPassword isBiddingView={false} isResetPassword={true} />;
};

export default ResetPassword;
